/**
 * Unified Permission System
 *
 * Mirrors the backend PermissionKey enum from @system/model/system-permission.ts
 * Uses SCREAMING_SNAKE_CASE string keys instead of numeric enum values.
 */

export enum PermissionLevel {
  ALL = 'All',
  BROKERAGE = 'Brokerage',
  OFFICE = 'Office',
  INDIVIDUAL = 'Individual',
}

/**
 * Unified permission keys - matches backend PermissionKey enum.
 * RESO permission names normalized to SCREAMING_SNAKE_CASE.
 */
export enum PermissionKey {
  // View access (synthetic - enables form access)
  VIEW_LISTING = 'VIEW_LISTING',

  // Access & Reports
  ACCESS_OWN_OFFICE_LISTINGS_AND_RANKING_AND_PRODUCTION_REPORTS = 'ACCESS_OWN_OFFICE_LISTINGS_AND_RANKING_AND_PRODUCTION_REPORTS',
  ACCESS_SPECIFIC_OFFICES_LISTINGS_AND_RANKING_AND_PRODUCTION_REPORTS = 'ACCESS_SPECIFIC_OFFICES_LISTINGS_AND_RANKING_AND_PRODUCTION_REPORTS',
  ASST_STAFF_ACCESS_TO_ALL_BROKERAGE_LISTINGS_RANKING_AND_PRODUCTION_REPORTS = 'ASST_STAFF_ACCESS_TO_ALL_BROKERAGE_LISTINGS_RANKING_AND_PRODUCTION_REPORTS',
  ASST_STAFF_ACCESS_TO_SPECIFIC_OFFICES_LISTINGS_RANKING_AND_PRODUCTION_REPORTS = 'ASST_STAFF_ACCESS_TO_SPECIFIC_OFFICES_LISTINGS_RANKING_AND_PRODUCTION_REPORTS',
  ASST_STAFF_ACCESS_TO_OWN_OFFICE_LISTINGS_RANKING_AND_PRODUCTION_REPORTS = 'ASST_STAFF_ACCESS_TO_OWN_OFFICE_LISTINGS_RANKING_AND_PRODUCTION_REPORTS',

  // Open House
  ADD_OR_EDIT_BROKER_OPEN_HOUSE = 'ADD_OR_EDIT_BROKER_OPEN_HOUSE',
  ADD_OR_EDIT_PUBLIC_OPEN_HOUSE = 'ADD_OR_EDIT_PUBLIC_OPEN_HOUSE',

  // Dates
  DECREASE_LISTING_EXPIRATION_DATE = 'DECREASE_LISTING_EXPIRATION_DATE',
  EDIT_LISTING_EXPIRATION_WITHDRAWN_DATE = 'EDIT_LISTING_EXPIRATION_WITHDRAWN_DATE',
  EXTEND_LISTING_EXPIRATION_DATE = 'EXTEND_LISTING_EXPIRATION_DATE',

  // Remarks
  EDIT_REMARKS_AGENT = 'EDIT_REMARKS_AGENT',
  EDIT_REMARKS_BROKERAGE = 'EDIT_REMARKS_BROKERAGE',
  EDIT_REMARKS_CONFIRMATION_AND_ENTRY_INFORMATION = 'EDIT_REMARKS_CONFIRMATION_AND_ENTRY_INFORMATION',
  EDIT_REMARKS_CUSTOMER_PUBLIC_GREEN = 'EDIT_REMARKS_CUSTOMER_PUBLIC_GREEN',
  EDIT_REMARKS_OFFICE = 'EDIT_REMARKS_OFFICE',
  EDIT_REMARKS_SHOWING_INSTRUCTIONS = 'EDIT_REMARKS_SHOWING_INSTRUCTIONS',

  // Showings
  EDIT_SHOWING_OFFICE_SETUP = 'EDIT_SHOWING_OFFICE_SETUP',
  ALLOW_SHOWINGS_TO_BE_MANAGED_IN_BRIGHT_MLS = 'ALLOW_SHOWINGS_TO_BE_MANAGED_IN_BRIGHT_MLS',
  SHOWING_TIME_ONLY = 'SHOWING_TIME_ONLY',
  SHOWING_HUB_PROVIDERS_ONLY = 'SHOWING_HUB_PROVIDERS_ONLY',
  ALL_SHOWING_PROVIDERS = 'ALL_SHOWING_PROVIDERS',

  // Media
  ADD_EDIT_VIRTUAL_TOURS = 'ADD_EDIT_VIRTUAL_TOURS',
  UPLOAD_DOCUMENTS = 'UPLOAD_DOCUMENTS',
  UPLOAD_LISTING_PHOTOS = 'UPLOAD_LISTING_PHOTOS',

  // News & Info
  MANAGE_BRIGHT_MLS_NEWS = 'MANAGE_BRIGHT_MLS_NEWS',
  MONITOR_OFFICE_ASSOCIATION_NEWS = 'MONITOR_OFFICE_ASSOCIATION_NEWS',
  MANAGE_ASSOCIATION_NEWS = 'MANAGE_ASSOCIATION_NEWS',
  MANAGE_OFFICE_NEWS = 'MANAGE_OFFICE_NEWS',

  // Error & Debug
  VIEW_EXTENDED_ERROR_INFORMATION = 'VIEW_EXTENDED_ERROR_INFORMATION',

  // Listing Status
  CANCEL_LISTINGS = 'CANCEL_LISTINGS',
  EDIT_LISTING_STATUS = 'EDIT_LISTING_STATUS',
  DELETE_LISTING = 'DELETE_LISTING',

  // Publishing
  PUBLISH_CHANGES_TO_EXISTING_LISTINGS = 'PUBLISH_CHANGES_TO_EXISTING_LISTINGS',
  PUBLISH_NEW_LISTINGS = 'PUBLISH_NEW_LISTINGS',

  // Edit Permissions
  CAN_EDIT_LISTINGS = 'CAN_EDIT_LISTINGS',
  DATA_CORRECTIONS = 'DATA_CORRECTIONS',
  ADD_EDIT_ALL_OTHER_LISTING_DATA = 'ADD_EDIT_ALL_OTHER_LISTING_DATA',
  EDIT_CURRENT_PRICE = 'EDIT_CURRENT_PRICE',
  EDIT_LISTING_CONTRACT_DATE = 'EDIT_LISTING_CONTRACT_DATE',
  EDIT_COOPERATIVE_COMPENSATION = 'EDIT_COOPERATIVE_COMPENSATION',
  EDIT_CONCESSION_IN_PRICE = 'EDIT_CONCESSION_IN_PRICE',

  // Impersonation
  CAN_IMPERSONATE = 'CAN_IMPERSONATE',
  CAN_WORK_ON_BEHALF_OF = 'CAN_WORK_ON_BEHALF_OF',

  // Listing Management
  ACCESS_LISTING_MANAGEMENT = 'ACCESS_LISTING_MANAGEMENT',
  ADD_NON_MEMBER_LISTINGS = 'ADD_NON_MEMBER_LISTINGS',
  ADD_OR_EDIT_UNPUBLISHED_LISTINGS = 'ADD_OR_EDIT_UNPUBLISHED_LISTINGS',
  SELF_SELECT_WHO_CAN_EDIT_HIS_HER_LISTINGS = 'SELF_SELECT_WHO_CAN_EDIT_HIS_HER_LISTINGS',
  SELF_SELECT_WHO_CAN_MANAGE_HIS_HER_CLIENTS = 'SELF_SELECT_WHO_CAN_MANAGE_HIS_HER_CLIENTS',

  // Member Permissions
  EDIT_OTHER_MEMBERS_PERMISSIONS = 'EDIT_OTHER_MEMBERS_PERMISSIONS',
  EDIT_BRIGHT_MLS_EMPLOYEE_PERMISSION = 'EDIT_BRIGHT_MLS_EMPLOYEE_PERMISSION',

  // Reports
  LISTING_PERFORMANCE_REPORT = 'LISTING_PERFORMANCE_REPORT',
  PRODUCTIVITY_REPORTS = 'PRODUCTIVITY_REPORTS',

  // Member Types
  APPRAISER = 'APPRAISER',
  CORPORATE_OR_ASSOCIATION_EXECUTIVE = 'CORPORATE_OR_ASSOCIATION_EXECUTIVE',
  GOVERNMENT_AGENCY = 'GOVERNMENT_AGENCY',
  BRIGHT_MLS_EMPLOYEE_ADMIN = 'BRIGHT_MLS_EMPLOYEE_ADMIN',
  BRIGHT_MLS_EMPLOYEE = 'BRIGHT_MLS_EMPLOYEE',
  BETA_TESTERS = 'BETA_TESTERS',

  // Access Levels - Asst/Staff
  ASST_STAFF_ACCESS_TO_ALL_BROKERAGE_LISTINGS = 'ASST_STAFF_ACCESS_TO_ALL_BROKERAGE_LISTINGS',
  ASST_STAFF_ACCESS_TO_SPECIFIC_OFFICES_LISTINGS = 'ASST_STAFF_ACCESS_TO_SPECIFIC_OFFICES_LISTINGS',
  ASST_STAFF_ACCESS_TO_OWN_OFFICE_LISTINGS = 'ASST_STAFF_ACCESS_TO_OWN_OFFICE_LISTINGS',
  ASST_STAFF_ACCESS_TO_PERSONAL_LISTINGS_ONLY = 'ASST_STAFF_ACCESS_TO_PERSONAL_LISTINGS_ONLY',
  ASST_STAFF_ACCESS_TO_PERSONAL_LISTINGS_RANKING_AND_PRODUCTION_REPORTS = 'ASST_STAFF_ACCESS_TO_PERSONAL_LISTINGS_RANKING_AND_PRODUCTION_REPORTS',

  // Access Levels - General
  ACCESS_ALL_BROKERAGE_LISTINGS = 'ACCESS_ALL_BROKERAGE_LISTINGS',
  ACCESS_ALL_BROKERAGE_LISTINGS_AND_RANKING_AND_PRODUCTION_REPORTS = 'ACCESS_ALL_BROKERAGE_LISTINGS_AND_RANKING_AND_PRODUCTION_REPORTS',
  ACCESS_SPECIFIC_OFFICES_LISTINGS = 'ACCESS_SPECIFIC_OFFICES_LISTINGS',
  ACCESS_OWN_OFFICE_LISTINGS = 'ACCESS_OWN_OFFICE_LISTINGS',
  ACCESS_PERSONAL_LISTINGS_ONLY = 'ACCESS_PERSONAL_LISTINGS_ONLY',
  ACCESS_PERSONAL_LISTINGS_AND_RANKING_AND_PRODUCTION_REPORTS = 'ACCESS_PERSONAL_LISTINGS_AND_RANKING_AND_PRODUCTION_REPORTS',
  NO_ACCESS = 'NO_ACCESS',
  VIEW_ONLY = 'VIEW_ONLY',
  LIMITED_ACCESS = 'LIMITED_ACCESS',

  // Teams
  TEAM_LEAD = 'TEAM_LEAD',
  TEAM_EDIT = 'TEAM_EDIT',
  SYNDICATE_LISTINGS_AS_TEAM_LEAD = 'SYNDICATE_LISTINGS_AS_TEAM_LEAD',

  // Features
  OFFER_MANAGEMENT = 'OFFER_MANAGEMENT',
  EARNEST_MONEY = 'EARNEST_MONEY',
  ALL = 'ALL',
  SENTRI_KEY = 'SENTRI_KEY',
  DUMMY_OPTION = 'DUMMY_OPTION',
}

/**
 * Permission object from backend
 */
export interface SystemPermission {
  key: PermissionKey;
  level: PermissionLevel;
  grantorKey?: string;
  ruleKey?: string;
}

/**
 * Permission result from /me endpoint
 */
export interface SystemPermissionResult {
  permissions: SystemPermission[];
  permissionMap: Record<string, true>;
  permissionKeyMap: Record<string, true>;
}

/**
 * Permission check interface for allowed()
 */
export interface PermissionCheck {
  key: PermissionKey;
  level?: PermissionLevel;
}

/**
 * Minimal listing info needed to check edit permissions.
 * Matches RESO field names from PropertyDetails / PropertyField.
 */
export interface ListingPermissionInfo {
  LIST_AGENT_MLS_ID?: string;
  LIST_OFFICE_KEY?: string;
  LIST_OFFICE_BROKER_OF_RECORD_KEY?: string;
}
